import type { ThreeDTocSearchResult } from '@/interfaces/library'

const modelEntityTypes = new Set([
  '3dmodel',
  'threedmodel'
])

const snapshotEntityTypes = new Set([
  '3dmodelsnapshot',
  '3dsnapshot',
  'snapshot',
  'threedmodelsnapshot',
  'threedsnapshot'
])

export function normalizeTocSearchEntityType (value: unknown) {
  return String(value ?? '').toLocaleLowerCase().replace(/[^a-z0-9]/g, '')
}

export function isPageTocSearchResult (result?: ThreeDTocSearchResult | null) {
  return normalizeTocSearchEntityType(result?.entityType) === 'page'
}

export function isThreeDModelTocSearchResult (result?: ThreeDTocSearchResult | null) {
  return modelEntityTypes.has(normalizeTocSearchEntityType(result?.entityType))
}

export function isThreeDSnapshotTocSearchResult (result?: ThreeDTocSearchResult | null) {
  return snapshotEntityTypes.has(normalizeTocSearchEntityType(result?.entityType))
}

export function getHighlightedFields (result?: ThreeDTocSearchResult | null) {
  const highlightedFields = result?.highlightedFields
  if (!highlightedFields) return {}

  if (Array.isArray(highlightedFields)) {
    return highlightedFields.reduce<Record<string, string[]>>((fields, entry) => ({
      ...fields,
      ...entry
    }), {})
  }

  return highlightedFields
}

export function getThreeDTocSearchResultKey (result?: ThreeDTocSearchResult | null) {
  if (result?.entityId == null) return ''
  if (isThreeDModelTocSearchResult(result)) return `model:${String(result.entityId)}`
  if (isThreeDSnapshotTocSearchResult(result)) return `snapshot:${String(result.entityId)}`
  return ''
}

export function isMappedPartHighlightedField (field: string) {
  return /^(?:part(?:_|:|\.)|tag:)/i.test(field)
}

export function getThreeDHighlightedContent (
  result: ThreeDTocSearchResult | null | undefined,
  locale: unknown
) {
  const fields = getHighlightedFields(result)
  const localeSuffix = String(locale ?? '').replace('-', '_')
  const highlightedNameHtml = fields[`name_${localeSuffix}`]?.[0] || null
  const nonNameFields = Object.keys(fields).filter(field => !field.startsWith('name_'))

  if (nonNameFields.length > 1) {
    return {
      helpTextKey: 'searchMatchHelp.fields',
      helpTextParams: { count: nonNameFields.length },
      helpTextHtml: null,
      highlightedNameHtml
    }
  }

  const onlyField = nonNameFields[0]
  if (!onlyField) {
    return { helpTextKey: '', helpTextParams: undefined, helpTextHtml: null, highlightedNameHtml }
  }

  if (isMappedPartHighlightedField(onlyField)) {
    return {
      helpTextKey: 'searchMatchHelp.part',
      helpTextParams: undefined,
      helpTextHtml: null,
      highlightedNameHtml
    }
  }

  const descriptionKey = `description_${localeSuffix}`
  return {
    helpTextKey: '',
    helpTextParams: undefined,
    helpTextHtml: onlyField === descriptionKey ? fields[onlyField]?.[0] || null : null,
    highlightedNameHtml
  }
}
